import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query';
import { RoutePending } from './components/route-pending/route-pending';
import { routeTree } from './routeTree.gen';
import { getQueryClient } from './lib/queryClient';
// boneyard: registers the captured skeleton bones (generated into app/bones by
// `bones:build`). Loaded in EVERY environment, not just dev: without the
// registry `getRegisteredBones()` returns undefined and boneyard renders the
// caller's `fallback` - the generic gray boxes - so staging and production
// showed skeletons that did not match the real pages at all. It stays a dynamic
// import so the geometry lands in its own chunk after boot, not in the entry,
// and the browser guard keeps it out of the SSR bundle.
if (typeof window !== 'undefined') {
  void import('./bones/registry');
}

/**
 * Create Router Instance
 * Creates a new router instance for TanStack Start
 *
 * - QueryClient is created INSIDE the router factory
 * - Each SSR request gets a fresh QueryClient instance
 * - Client-side reuses the same QueryClient instance
 * - This ensures proper data isolation between requests
 *
 * @see https://tanstack.com/start/latest/docs/framework/react/ssr
 */
export function createRouter() {
  // Fresh QueryClient per server request (singleton on client) — see lib/queryClient.ts.
  // Created inside the factory so SSR requests don't share a QueryClient.
  const queryClient = getQueryClient();

  const router = createTanStackRouter({
    routeTree,
    // queryClient in context so route loaders can prefetch via context.queryClient
    context: { queryClient },
    // Preload on hover/focus for better UX
    defaultPreload: 'intent',
    defaultPreloadDelay: 100,
    // CRITICAL for TanStack Query: disable the router's own preload cache so Query
    // controls data freshness. (defaultStaleTime below governs loader return values.)
    defaultPreloadStaleTime: 0,
    // ✅ SSR OPTIMIZATION: Trust server-rendered data, don't refetch on hydration
    defaultStaleTime: Infinity,
    defaultViewTransition: false,
    // Non-blocking client navigation: once a navigation stays pending past
    // this threshold, the route's pendingComponent (or RoutePending for
    // routes without one) replaces the old page. 0ms is deliberate (audit
    // round 4): the destination's own page template — header chrome and
    // content skeleton (page-template-pending.tsx) — must appear the moment
    // a navigation will take any time at all; holding the old page 100ms is
    // exactly the "headers don't appear immediately" feel. Cached
    // navigations are unaffected: a loader that resolves from cache has
    // status success before the threshold applies, so the old page swaps
    // STRAIGHT to content — no skeleton, no pend. defaultPendingMinMs keeps
    // genuinely-shown skeletons on screen briefly so a barely-slow load
    // can't flash.
    defaultPendingMs: 0,
    defaultPendingMinMs: 170,
    defaultPendingComponent: RoutePending,
  });

  // Owns SSR dehydration/hydration of the query cache. Replaces the manual
  // dehydrate/hydrate hooks, which trip TanStack Start's serializability check
  // on React Query's `mutationKey: unknown[]`. wrapQueryClient:false — the
  // QueryClientProvider stays in __root.tsx (correct placement inside <html>).
  setupRouterSsrQueryIntegration({
    router,
    queryClient,
    wrapQueryClient: false,
  });

  return router;
}

/**
 * TanStack Start Router Export
 * TanStack Start expects a getRouter function for SSR
 */
export function getRouter() {
  return createRouter();
}

/**
 * TypeScript Module Augmentation
 * Properly types the router context for all routes
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}
